import { useEffect, useState, useMemo, useRef } from 'react';
import {
    GridFilterModel,
    GridSortDirection,
    GridSortItem,
} from '@mui/x-data-grid';
import { useLazyGetSupplierDevicesEsQuery } from 'store/services/device';
import { getEsDirectionFromMui } from 'store/services/es-query-helpers';
import { useGlobalSearch, useUserSession } from 'hooks';
import { useAuth } from 'hooks/useAuth';
import { ElasticsearchDeviceBodyConfig } from 'types/ElasticsearchQueries';
import {
    DefaultEsQueryConfig,
    defaultLiveMonitoringListSortDirection,
    defaultLiveMonitoringListSortField,
} from 'lib/constants';
import { TanksListView } from './components/TanksListView/TanksListView';
import styles from './LiveMonitoring.module.scss';
import { DataTableReportPreset } from 'components/data/DataTable/components/GridToolbarPresetSelector/GridToolbarPresetSelector';
import { FilterBar } from 'components/common/FilterBar/FilterBar';
import { updateUserGroupSession, updateUserSession } from 'lib/firebase';
import { UserDeviceSession } from 'types/User/firebaseTypes';
import { useDispatch } from 'react-redux';
import { setViewAsSupplier } from 'store/features/viewAsSupplier';
import { useSupplierGroups } from 'hooks/useSupplierGroups';
import { SupplierDeliveryType, SupplierGroup } from 'types';
import { useSupplierDeliveryTypes } from 'hooks/useSupplierDeliveryTypes';
import { Box } from '@mui/material';
import { List, Map } from '@mui/icons-material';
import { useLocation } from 'react-router-dom';
import { DeviceMap } from 'components/data/DeviceMap/DeviceMap';
import { TabContext } from '@mui/lab';
import { TuTab } from 'components/common/TuTab/TuTab';
import { TuTabs } from 'components/common/TuTabs/TuTabs';
import { TuTabPanel } from 'components/common/TuTabPanel/TuTabPanel';

export interface LiveMonitoringProps {
    /** A supplierId for the supplier that an admin user wants to view data for. */
    viewAsSupplier?: string;
}

export function LiveMonitoring({ viewAsSupplier }: LiveMonitoringProps) {
    const dispatch = useDispatch();
    const { user } = useAuth();
    const [query, { data, isLoading, isFetching }] =
        useLazyGetSupplierDevicesEsQuery();
    const initialRender = useRef(true);
    const [didAddDevice, setDidAddDevice] = useState(false);
    const [createdDeviceId, setCreatedDeviceId] = useState('');
    const [totalNumberOfDevices, setTotalNumberOfDevices] = useState(0);

    // Data Filtering
    const session = useUserSession();
    const limit = DefaultEsQueryConfig.limit ?? 0;
    const [currentPage, setCurrentPage] = useState<number>(0);
    const [pageSize, setPageSize] = useState<number>(limit);
    const [sortModel, setSortModel] = useState<GridSortItem[]>([
        {
            field: defaultLiveMonitoringListSortField,
            sort: defaultLiveMonitoringListSortDirection,
        },
    ]);
    const [sortField, setSortField] = useState<string>();
    const [sortDirection, setSortDirection] = useState<GridSortDirection>();
    const [searchModel, setSearchModel] = useState<GridFilterModel>();
    const [currentPreset, setCurrentPreset] =
        useState<DataTableReportPreset | null>(null);
    const { globalSearchTerm } = useGlobalSearch();
    const groups = useSupplierGroups();
    const deliveryTypes = useSupplierDeliveryTypes();

    const [queryConfig, setQueryConfig] =
        useState<ElasticsearchDeviceBodyConfig>();

    const queryOptions = useMemo(
        () => ({
            currentPage,
            pageSize,
            sortField,
            sortDirection,
            searchModel,
        }),
        [currentPage, pageSize, sortField, sortDirection, searchModel]
    );

    const [monitorTypeFilters, setMonitorTypeFilters] = useState<
        Record<string, boolean>
    >({});
    const [activeStatusFilters, setActiveStatusFilters] = useState<
        Record<string, boolean>
    >({});
    const [consumptionTypeFilters, setConsumptionTypeFilters] = useState<
        Record<string, boolean>
    >({});
    const [deliveryTypeFilters, setDeliveryTypeFilters] = useState<
        Record<string, boolean>
    >({});
    const [groupFilters, setGroupFilters] = useState<Record<string, boolean>>(
        {}
    );

    const [deviceFilters, setDeviceFilters] =
        useState<Partial<UserDeviceSession['filters']>>();

    const location = useLocation();
    const [currentView, setCurrentView] = useState<string>('list');

    const changeTab = (tabName: string) => {
        setCurrentView(tabName);
        if (!viewAsSupplier)
            window.history.replaceState(null, '', `/live-reporting/${tabName}`);
        else
            window.history.replaceState(
                null,
                '',
                `/supplier-details/${viewAsSupplier}/live-reporting/${tabName}`
            );
    };

    // Update tab view (e.g., map vs list etc).
    useEffect(() => {
        const urlParts = location.pathname.split('/');
        let currentTab = urlParts.pop() ?? '';

        const tabs = ['list', 'map'];
        // If the last part of the URL does not match a valid tab/sub page, default to the list tab.
        if (!tabs.includes(currentTab)) {
            currentTab = 'list';
        }

        setCurrentView(currentTab);
    }, [location.pathname, session?.device, user?.uid]);

    // Update user session to reflect current active view.
    useEffect(() => {
        if (currentView && user?.uid && session?.device) {
            const newSessionEntry: UserDeviceSession = {
                ...session.device,
                activeView: currentView,
            };
            updateUserSession(user.uid, 'device', newSessionEntry);
        }
    }, [currentView, session?.device, user?.uid]);

    // Set configs based on user session.
    useEffect(() => {
        // Set device filters config based on user session.
        if (session?.device) {
            const filters = session.device.filters;
            setMonitorTypeFilters({
                virtual: !!filters.misc.virtual ? filters.misc.virtual : false,
                physical: !!filters.misc.physical
                    ? filters.misc.physical
                    : false,
            });
            setActiveStatusFilters({
                active: !!filters.misc.active ? filters.misc.active : false,
                inactive: !!filters.misc.inactive
                    ? filters.misc.inactive
                    : false,
            });
            setConsumptionTypeFilters(filters.consumptionType);
            setDeliveryTypeFilters(filters.delivery_type ?? {});
        }
        // Set group filters based on user session.
        if (groups) {
            const groupFilters: Record<string, boolean> = {};

            // Add all possible groups. Default to off.
            groups.forEach((group: SupplierGroup) => {
                groupFilters[group.name] = false;
            });

            // Turn on any groups included in the session groups object.
            session?.groups?.forEach(
                (group: string) => (groupFilters[group] = true)
            );
            setGroupFilters(groupFilters);
        }

        // Set delivery type filters based on user session.
        if (deliveryTypes) {
            const deliveryTypeFilters: Record<string, boolean> = {
                ...(session?.device?.filters.delivery_type ?? {}),
            };

            // Add remaining possible delivery types that are not already in the session object and turn these ones off by default.
            deliveryTypes.forEach((deliveryType: SupplierDeliveryType) => {
                if (!(deliveryType.deliveryTypeName in deliveryTypeFilters))
                    deliveryTypeFilters[deliveryType.deliveryTypeName] = false;
            });

            setDeliveryTypeFilters(deliveryTypeFilters);
        }

        // Set sort field based on user session.
        if (session?.device?.sort) {
            setSortField(
                session.device.sort.currentSortField ??
                    defaultLiveMonitoringListSortField
            );
        }

        // Set sort direction based on user session.
        if (
            session?.device?.sort?.currentSortDirection === 'asc' ||
            session?.device?.sort?.currentSortDirection === 'desc'
        ) {
            setSortDirection(session.device.sort.currentSortDirection);
        } else {
            setSortDirection(defaultLiveMonitoringListSortDirection);
        }

        // Set view config based on user session.
        if (session?.device?.activeView) {
            setCurrentView(session.device.activeView ?? 'list');
        }
    }, [session, groups, deliveryTypes]);

    // Update ES query params.
    useEffect(() => {
        setQueryConfig({
            limit: queryOptions.pageSize,
            offset: queryOptions.currentPage * queryOptions.pageSize,
            direction: getEsDirectionFromMui(queryOptions.sortDirection ?? ''),
            sortField: queryOptions.sortField,
            search: globalSearchTerm ?? '',
            sessionFilters: deviceFilters,
            groups: groupFilters,
            isAdminUser: user?.protected?.supplierId?.toString() === '0',
        });
    }, [
        queryOptions,
        globalSearchTerm,
        deviceFilters,
        groupFilters,
        user?.protected?.supplierId,
    ]);

    // Reset page when search or filters change.
    useEffect(() => {
        setCurrentPage(0);
    }, [globalSearchTerm, deviceFilters]);

    const [refetchDevices, setRefetchDevices] = useState(false);

    // Get all monitors for the supplier.
    useEffect(() => {
        if (user?.protected) {
            query({
                userUid: user?.uid ?? '',
                supplierId: viewAsSupplier ?? user?.protected?.supplierId ?? '',
                queryConfig: queryConfig,
            });

            dispatch(
                setViewAsSupplier(viewAsSupplier ?? user?.protected.supplierId)
            );
        }

        if (refetchDevices) {
            setRefetchDevices(false);
        }

        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [
        refetchDevices,
        user?.protected,
        user?.uid,
        queryConfig,
        viewAsSupplier,
        dispatch,
        query,
    ]);

    // Update total number of devices to help us know when a new device has been added.
    useEffect(() => {
        if (data?.total) setTotalNumberOfDevices(data.total);
    }, [data?.total]);

    // Query for supplier's devices to get updated list after a new device is added.
    useEffect(() => {
        if (didAddDevice && createdDeviceId) {
            let numberOfRetries = 0;
            // Make a device-search query every 2000 ms until the newly created device is added to the results.
            const interval = setInterval(() => {
                query({
                    userUid: user?.uid ?? '',
                    supplierId:
                        viewAsSupplier ?? user?.protected?.supplierId ?? '',
                    queryConfig: queryConfig,
                }).then((results) => {
                    const newNumberOfDevices = results.data?.total || 0;
                    const found = newNumberOfDevices > totalNumberOfDevices;

                    if (found || numberOfRetries > 0) {
                        // Reset state variables so additional devices can be added + clear interval to stop the query loop.
                        setDidAddDevice(false);
                        setTotalNumberOfDevices(newNumberOfDevices);
                        setCreatedDeviceId('');
                        clearInterval(interval);
                    } else {
                        numberOfRetries++;
                    }
                });
            }, 2000);
        }

        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [didAddDevice]);

    // Update sort field + direction based on user use of preset options. Update user session accordingly.
    // Use whichever is newer between currentPreset and sortModel changes for the ES query sorting and direction params.
    useEffect(() => {
        if (currentPreset) {
            setSortDirection(currentPreset.sort.order);
            setSortField(currentPreset.sort.by);

            // Update the user session to reflect new sort config.
            if (user?.uid && session?.device) {
                const newSessionEntry: UserDeviceSession = {
                    ...session.device,
                    sort: {
                        currentSortDirection: currentPreset.sort.order ?? '',
                        currentSortField: currentPreset.sort.by,
                    },
                };
                updateUserSession(user.uid, 'device', newSessionEntry);
            }
        }
    }, [currentPreset, session?.device, user?.uid]);

    // Update sort field + direction based on user UI interaction. Update user session accordingly.
    // Use whichever is newer between currentPreset and sortModel changes for the ES query sorting and direction params.
    useEffect(() => {
        if (sortModel[0]) {
            setSortDirection(sortModel[0].sort);
            setSortField(sortModel[0].field);

            // Update the user session to reflect new sort config.
            if (user?.uid && session?.device) {
                const newSessionEntry: UserDeviceSession = {
                    ...session.device,
                    sort: {
                        currentSortDirection: sortModel[0].sort ?? '',
                        currentSortField: sortModel[0].field,
                    },
                };
                updateUserSession(user.uid, 'device', newSessionEntry);
            }
        }
    }, [session?.device, sortModel, user?.uid]);

    // Make network call to update device session filters.
    useEffect(() => {
        setDeviceFilters({
            delivery_type: deliveryTypeFilters,
            consumptionType: consumptionTypeFilters,
            misc: {
                ...activeStatusFilters,
                ...monitorTypeFilters,
            },
        });

        if (user?.uid && session?.device) {
            if (initialRender.current) {
                initialRender.current = false;
            } else {
                const newSessionEntry: UserDeviceSession = {
                    ...session.device,
                    filters: {
                        consumptionType: consumptionTypeFilters,
                        consumptionTypeEnabled:
                            session.device.filters.consumptionTypeEnabled ??
                            false,
                        delivery_type: deliveryTypeFilters,
                        delivery_type_enabled:
                            session.device.filters.delivery_type_enabled ??
                            false,
                        misc: { ...monitorTypeFilters, ...activeStatusFilters },
                        groupEnabled:
                            session.device.filters?.groupEnabled ?? false,
                    },
                };
                updateUserSession(user.uid, 'device', newSessionEntry);
            }
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [
        consumptionTypeFilters,
        monitorTypeFilters,
        activeStatusFilters,
        deliveryTypeFilters,
    ]);

    // Make network call to update group session filters.
    useEffect(() => {
        if (user?.uid && groupFilters) {
            if (initialRender.current) {
                initialRender.current = false;
            } else {
                const newSessionEntry: string[] = [];
                Object.entries(groupFilters).forEach(
                    (entry: [string, boolean]) => {
                        const groupName: string = entry[0];
                        const groupActive: boolean = entry[1];
                        if (groupActive) newSessionEntry.push(groupName);
                    }
                );
                updateUserGroupSession(user.uid, newSessionEntry);
            }
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [groupFilters]);

    return (
        <div data-testid="LiveMonitoring" className={styles.container}>
            <TabContext value={currentView}>
                <Box className={styles.tabsContainer}>
                    <TuTabs
                        value={currentView}
                        aria-label="Change view"
                        variant="scrollable"
                        scrollButtons="auto"
                    >
                        <TuTab
                            label="List"
                            value="list"
                            icon={<List />}
                            onClick={() => changeTab('list')}
                        />
                        <TuTab
                            label="Map"
                            value="map"
                            icon={<Map />}
                            onClick={() => changeTab('map')}
                        />
                    </TuTabs>
                </Box>
                {currentView === 'list' && (
                    <TuTabPanel value="list" className={styles.tabPanel}>
                        <FilterBar
                            filterGroups={[
                                {
                                    name: 'Monitor Type',
                                    filters: monitorTypeFilters,
                                    onUpdateFilters: setMonitorTypeFilters,
                                },
                                {
                                    name: 'Active',
                                    filters: activeStatusFilters,
                                    onUpdateFilters: setActiveStatusFilters,
                                },
                                {
                                    name: 'Usage Type',
                                    filters: consumptionTypeFilters,
                                    onUpdateFilters: setConsumptionTypeFilters,
                                },
                                {
                                    name: 'Delivery Type',
                                    filters: deliveryTypeFilters,
                                    onUpdateFilters: setDeliveryTypeFilters,
                                },
                                {
                                    name: 'Groups',
                                    filters: groupFilters,
                                    onUpdateFilters: setGroupFilters,
                                },
                            ]}
                        />
                        <TanksListView
                            data={data}
                            loading={isLoading}
                            fetching={isFetching}
                            onFilterModelChange={setSearchModel}
                            onPageChange={setCurrentPage}
                            onPageSizeChange={setPageSize}
                            onSortModelChange={setSortModel}
                            onCurrentPresetChange={setCurrentPreset}
                            gridSortModel={[
                                { sort: sortDirection, field: sortField ?? '' },
                            ]}
                            queryConfig={queryConfig}
                            page={currentPage}
                            onDeviceCreated={setDidAddDevice}
                            setCreatedDeviceId={setCreatedDeviceId}
                            refetchListView={setRefetchDevices}
                            viewAsSupplier={
                                viewAsSupplier ?? user?.protected?.supplierId
                            }
                        />
                    </TuTabPanel>
                )}
                {currentView === 'map' && (
                    <TuTabPanel value="map" className={styles.mapTabPanel}>
                        <FilterBar
                            filterGroups={[
                                {
                                    name: 'Monitor Type',
                                    filters: monitorTypeFilters,
                                    onUpdateFilters: setMonitorTypeFilters,
                                },
                                {
                                    name: 'Active',
                                    filters: activeStatusFilters,
                                    onUpdateFilters: setActiveStatusFilters,
                                },
                                {
                                    name: 'Usage Type',
                                    filters: consumptionTypeFilters,
                                    onUpdateFilters: setConsumptionTypeFilters,
                                },
                                {
                                    name: 'Delivery Type',
                                    filters: deliveryTypeFilters,
                                    onUpdateFilters: setDeliveryTypeFilters,
                                },
                                {
                                    name: 'Groups',
                                    filters: groupFilters,
                                    onUpdateFilters: setGroupFilters,
                                },
                            ]}
                        />
                        <DeviceMap
                            queryConfig={queryConfig}
                            viewAsSupplier={viewAsSupplier}
                        />
                    </TuTabPanel>
                )}
            </TabContext>
        </div>
    );
}
